import { useMemo } from 'react';

import { Link } from 'react-router-dom';

import { Flex, Select, Table } from 'antd';
import type { TableColumnsType } from 'antd';

import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import { EMPTY_MESSAGES, PLACEHOLDER_MESSAGES, ROUTES, VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { highlightText } from '@/shared/lib/text/highlight';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { ColumnVisibilityButton } from '@/shared/ui/ColumnVisibilityButton';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { SearchInput } from '@/shared/ui/SearchInput';
import { TableEmpty } from '@/shared/ui/TableEmpty';

import { useTagCategorySearch, useTagCategoryVisibleColumn } from '@/features/tags/model';
import { useTagCategorySelect } from '@/features/tags/model';
import { useTagCategoryTableData } from '@/features/tags/model';

import styles from './TagCategoryTable.module.css';

type TableRowData = {
  key: string;
  id: string;
  name: string;
  displayName: string;
  description: string;
  fullyQualifiedName: string;
  provider: string;
};

type TagCategoryTableProps = {
  selectedTagCategory: string;
};

export const TagCategoryTable = ({ selectedTagCategory }: TagCategoryTableProps) => {
  const { query, debouncedQuery, handleSearch } = useTagCategorySearch();
  const { selectOptions, selectedType, handleSelectType } = useTagCategorySelect();

  const { validateLength, handleCompositionStart, handleCompositionEnd } = useSearchLengthValidation({
    maxLength: 30,
    warningMessage: VALIDATION_MESSAGES.TAG_SEARCH_MAX_LENGTH,
    debounceMs: 1000,
  });

  const provider = selectedType === 'all' ? undefined : selectedType;

  const {
    data: dataSource,
    total,
    isLoading,
    paginationConfig,
    scrollConfig,
  } = useTagCategoryTableData({
    selectedTagCategory,
    debouncedQuery,
    provider,
  });

  const columns = useMemo<TableColumnsType<TableRowData>>(
    () => [
      {
        title: '태그',
        dataIndex: 'name',
        key: 'name',
        width: '150px',
        render: (text: string, record: TableRowData) => {
          return (
            <EllipsisText text={text ?? '-'}>
              <Link to={`${ROUTES.TAGS_DETAIL}/${encodeURIComponent(record.fullyQualifiedName)}`}>
                {highlightText(text ?? '-', debouncedQuery, { className: 'highlight' })}
              </Link>
            </EllipsisText>
          );
        },
      },
      {
        title: '표시 이름',
        dataIndex: 'displayName',
        key: 'displayName',
        width: '150px',
        render: (text: string) => {
          return (
            <EllipsisText text={text ?? '-'}>
              {highlightText(text ?? '-', debouncedQuery, { className: 'highlight' })}
            </EllipsisText>
          );
        },
      },
      {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        width: total > 0 ? 450 : 'auto',
        render: (description: string) => (
          <AssetDescription
            description={description}
            mode="expandable"
            highlightQuery={debouncedQuery}
            highlightClassName="highlight"
          />
        ),
      },
    ],
    [total, debouncedQuery]
  );

  const allColumnsMap = useMemo(
    () =>
      columns.reduce(
        (acc, col) => {
          if (col.key) {
            acc[col.key as string] = { title: col.title as string };
          }
          return acc;
        },
        {} as Record<string, { title: string }>
      ),
    [columns]
  );

  const {
    columns: filterColumns,
    isVisibleColumns,
    defaultColumnOrder,
    defaultVisibleColumns,
  } = useTagCategoryVisibleColumn({ columns });

  return (
    <div className={styles.tablesTab}>
      <div className={styles.toolbar}>
        <SearchInput
          className={styles.searchInput}
          placeholder={PLACEHOLDER_MESSAGES.TAG_SEARCH}
          value={query}
          onChange={(e) => {
            const validatedValue = validateLength(e.target.value);
            const syntheticEvent = {
              ...e,
              target: { ...e.target, value: validatedValue },
            } as React.ChangeEvent<HTMLInputElement>;
            handleSearch(syntheticEvent);
          }}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          maxLength={30}
        />
        <Flex wrap="wrap" gap={12}>
          <Select options={selectOptions} value={selectedType} onChange={handleSelectType} style={{ width: 90 }} />
          <ColumnVisibilityButton
            defaultColumnOrder={defaultColumnOrder}
            defaultVisibleColumns={defaultVisibleColumns}
            allColumnsMap={allColumnsMap}
          />
        </Flex>
      </div>
      <Table<TableRowData>
        columns={filterColumns}
        dataSource={isVisibleColumns ? dataSource : []}
        pagination={paginationConfig}
        className={styles.tablesTable}
        scroll={{ x: dataSource.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
        sticky={true}
        locale={{
          emptyText: (
            <TableEmpty
              searchQuery={query}
              isVisibleColumns={!isVisibleColumns}
              description={EMPTY_MESSAGES.NO_CATEGORY_TAG}
            />
          ),
        }}
        loading={isLoading}
      />
    </div>
  );
};
